import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Coins, TrendingUp, Users, Zap, ExternalLink, Package, Sparkles } from "lucide-react";

const TIER_LABEL = { basic: "Basic", pro: "Pro", premium: "Premium" };
const TIER_COLOR = { basic: "#64748B", pro: "#0EA5E9", premium: "#F59E0B" };

export default function AgencyBilling() {
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, i, p] = await Promise.all([
        api.get("/admin/billing/summary"),
        api.get("/admin/billing/invoices"),
        api.get("/billing/plans"),
      ]);
      setSummary(s.data);
      setInvoices(i.data);
      setPlans(p.data);
    } catch (e) { toast.error("Errore caricamento billing"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !summary) return <div className="p-10 text-center text-sm">Caricamento…</div>;

  return (
    <div data-testid="agency-billing" className="space-y-6">
      <header>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight flex items-center gap-3">
          <Coins className="h-9 w-9 text-[color:var(--sp-brand)]" /> Fatturazione Piattaforma
        </h1>
        <p className="text-[color:var(--sp-text-secondary)] mt-2">Ricavi Salon Up dai tenant: MRR, ARR e storico pagamenti Stripe.</p>
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={TrendingUp} label="MRR" value={`€ ${summary.mrr.toLocaleString("it-IT")}`} sub="ricorrenti/mese" />
        <Kpi icon={Sparkles} label="ARR" value={`€ ${summary.arr.toLocaleString("it-IT")}`} sub="proiezione annua" color="var(--sp-success)" />
        <Kpi icon={Zap} label="Abbonamenti attivi" value={summary.active_subscriptions} />
        <Kpi icon={Coins} label="Ultimi 30gg" value={`€ ${summary.last_30d_revenue.toLocaleString("it-IT")}`} sub={`${summary.last_30d_invoices} fatture`} />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {Object.entries(summary.tenants_per_tier).map(([tier, count]) => (
          <div key={tier} className="sp-card p-5 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full opacity-10" style={{ background: TIER_COLOR[tier] }} />
            <span className="label-eyebrow">Tenant {TIER_LABEL[tier]}</span>
            <div className="font-display text-4xl font-black mt-3" style={{ color: TIER_COLOR[tier] }}>{count}</div>
            <div className="text-xs text-[color:var(--sp-text-muted)] mt-1">saloni in questo piano</div>
          </div>
        ))}
      </section>

      <section className="sp-card p-5">
        <h2 className="font-display text-xl font-black mb-4">Piani attivi (Stripe)</h2>
        {plans.length === 0 ? (
          <div className="text-sm text-[color:var(--sp-text-muted)]">Nessun piano configurato. Esegui <code>python -m scripts.setup_stripe</code>.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {plans.map((p) => (
              <div key={p.lookup_key} className="border border-[color:var(--sp-border)] rounded-xl p-4 flex items-start gap-3">
                <Package className="h-5 w-5 text-[color:var(--sp-brand)] mt-1" />
                <div className="flex-1">
                  <div className="font-bold">{p.product_name}</div>
                  <div className="text-xs text-[color:var(--sp-text-muted)]">{p.lookup_key}</div>
                  <div className="font-display text-2xl font-black mt-1">
                    € {p.amount.toLocaleString("it-IT")}
                    <span className="text-xs text-[color:var(--sp-text-muted)] font-normal ml-1">/{p.interval}</span>
                  </div>
                </div>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest text-white" style={{ background: TIER_COLOR[p.tier] }}>{TIER_LABEL[p.tier]}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="sp-card overflow-hidden">
        <div className="p-4 border-b border-[color:var(--sp-border)] flex items-center justify-between">
          <h2 className="font-display text-xl font-black">Ultime fatture Stripe</h2>
          <a href="https://dashboard.stripe.com" target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1 text-xs text-[color:var(--sp-brand)] font-bold hover:underline">
            Dashboard Stripe <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        {invoices.length === 0 ? (
          <div className="p-8 text-center text-sm text-[color:var(--sp-text-muted)]">Nessuna fattura registrata. Le fatture Stripe appariranno qui automaticamente dopo il primo pagamento.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--sp-subtle)]">
              <tr className="label-eyebrow">
                <th className="p-3 text-left">Data</th><th className="p-3 text-left">Salone</th><th className="p-3 text-right">Importo</th><th className="p-3 text-right">Link</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i.stripe_invoice_id} className="border-t border-[color:var(--sp-border)]">
                  <td className="p-3 text-xs">{i.paid_at ? new Date(i.paid_at * 1000).toLocaleDateString("it-IT") : "—"}</td>
                  <td className="p-3 font-medium">{i.tenant_name}</td>
                  <td className="p-3 text-right font-display font-black text-[color:var(--sp-brand)]">€ {(i.amount_paid || 0).toLocaleString("it-IT")}</td>
                  <td className="p-3 text-right">
                    {i.hosted_invoice_url && <a href={i.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-[color:var(--sp-brand)] inline-flex items-center gap-1 text-xs font-bold"><ExternalLink className="h-3 w-3" />Apri</a>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, color = "var(--sp-brand)" }) {
  return (
    <div className="sp-card p-5 sp-anim">
      <div className="flex items-center justify-between"><span className="label-eyebrow">{label}</span><Icon className="h-4 w-4" style={{ color }} /></div>
      <div className="font-display text-3xl font-black mt-3">{value}</div>
      {sub && <div className="text-xs text-[color:var(--sp-text-muted)] mt-1">{sub}</div>}
    </div>
  );
}
